// frontend/src/components/UpdateNumeroCasa.jsx
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import Swal from 'sweetalert2';
import { Home, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

export const UpdateNumeroCasa = ({ onComplete }) => {
    const { user, updateUser } = useAuth();
    const [numeroCasa, setNumeroCasa] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Si ya tiene unidad, no mostrar nada
    if (user?.unidade_id) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validar
        const numero = numeroCasa.trim();
        if (!numero) {
            setError('Informe o número da sua casa');
            return;
        }

        if (numero.length < 1) {
            setError('Número inválido');
            return;
        }

        setLoading(true);

        try {
            const response = await api.put('/user/update-numero-casa', {
                numero_casa: numero
            });

            // Actualizar usuario en el contexto
            if (updateUser) {
                updateUser(response.data.user);
            }

            // Mostrar éxito
            await Swal.fire({
                icon: 'success',
                title: '✅ Unidade vinculada!',
                text: `Seu número ${numero} foi vinculado com sucesso. Agora você pode publicar anúncios.`,
                timer: 3000,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });

            // Notificar al padre
            if (onComplete) {
                onComplete();
            }

            // Recargar la página para actualizar el estado
            setTimeout(() => {
                window.location.reload();
            }, 1000);

        } catch (error) {
            console.error('Erro ao atualizar número:', error);
            
            const message = error.response?.data?.message || 'Erro ao vincular unidade';
            setError(message);

            await Swal.fire({
                icon: 'error',
                title: '❌ Erro',
                text: message,
                confirmButtonColor: '#449879'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-yellow-200 p-6 mb-6 animate-slide-down">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-yellow-100 rounded-full">
                    <Home className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                        Complete seu cadastro
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Para publicar anúncios, você precisa vincular sua unidade.
                    </p>
                </div>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Número da sua casa / apartamento
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={numeroCasa}
                            onChange={(e) => {
                                setNumeroCasa(e.target.value);
                                setError('');
                            }}
                            placeholder="Ex: 123, 45B, Apt 101"
                            className={`flex-1 px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm ${
                                error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                            }`}
                            disabled={loading}
                            autoFocus
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-6 py-2.5 bg-yellow-500 text-white rounded-xl font-medium text-sm hover:bg-yellow-600 transition-all flex items-center gap-2 whitespace-nowrap ${
                                loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    Vincular
                                </>
                            )}
                        </button>
                    </div>
                    {error && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {error}
                        </p>
                    )}
                </div>

                {/* Información adicional */}
                <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-100">
                    <p className="text-xs text-yellow-700 flex items-start gap-2">
                        <span className="mt-0.5">💡</span>
                        <span>
                            O número da casa é usado para vincular você à sua unidade 
                            e controlar o limite de publicações (10 por mês).
                        </span>
                    </p>
                </div>
            </form>
        </div>
    );
};

export default UpdateNumeroCasa;